import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Shield, Users, CheckCircle } from 'lucide-react';

// Import your new photos
import PackingFacility from '../../../assets/photos/packing-facility-workers.jpg';
import QualityInspection from '../../../assets/photos/quality-inspection-team.jpg';
import ProduceInspection from '../../../assets/photos/produce-inspection-process.jpg';
import FacilityOperations from '../../../assets/photos/facility-operations-team.jpg';

interface IntroProps {
  onClickGetStarted: () => void;
}

const Intro: React.FC<IntroProps> = ({ onClickGetStarted }) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const heroImages = [
    PackingFacility,
    QualityInspection,
    ProduceInspection,
    FacilityOperations
  ];

  const features = [
    { icon: Shield, text: t('network.features.certified') },
    { icon: Users, text: t('network.features.expert') },
    { icon: CheckCircle, text: t('network.features.global') }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Background Image Carousel */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((image, index) => (
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: index === currentImageIndex ? 0.12 : 0 
            }}
            transition={{ duration: 1.5 }}
          >
            <img 
              src={image} 
              alt={`Hero background ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </motion.div>
        ))}
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-gray-50/70 z-10"></div>
      
      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center min-h-[75vh] md:min-h-[80vh]">
          
          {/* Left Column - Text Content */}
          <motion.div 
            className="space-y-6 md:space-y-8 text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >

            {/* Main Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#f84831] leading-tight">
                {t('intro.title')}
              </h1>
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-semibold text-[#2D2D2D] mt-3 md:mt-4 leading-relaxed">
                {t('intro.subtitle')}
              </h2>
            </motion.div>

            {/* Description */}
            <motion.p 
              className="text-base md:text-lg lg:text-xl text-[#2D2D2D]/80 leading-relaxed max-w-2xl mx-auto lg:mx-0 px-4 lg:px-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {t('intro.description')}
            </motion.p>

            {/* Feature Pills */}
            <motion.div 
              className="flex flex-wrap gap-2 md:gap-4 justify-center lg:justify-start px-4 lg:px-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center px-3 md:px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-[#2D2D2D] font-medium shadow-sm border border-white/30 text-xs md:text-sm"
                >
                  <feature.icon className="w-3 md:w-4 h-3 md:h-4 mr-1 md:mr-2 text-[#f84831]" />
                  <span className="hidden sm:inline">{feature.text}</span>
                  <span className="sm:hidden">{feature.text.split(' ')[0]}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2 md:pt-4 px-4 lg:px-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <button
                onClick={onClickGetStarted}
                className="group relative overflow-hidden bg-[#f84831] hover:bg-[#e63e26] text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="relative z-10 flex items-center justify-center">
                  {t('intro.getStarted')}
                  <ChevronRight className="w-4 md:w-5 h-4 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#f84831] to-[#ff6b54] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
              
              <button className="group border-2 border-[#2D2D2D] text-[#2D2D2D] hover:bg-[#2D2D2D] hover:text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all duration-300">
                {t('network.getStarted')}
              </button>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual Elements */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Main Image Card */}
            <div className="relative">
              <motion.div 
                className="relative overflow-hidden rounded-2xl shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={heroImages[currentImageIndex]} 
                  alt="Quality inspection process"
                  className="w-full h-96 md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Image Overlay Content */}
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h3 className="text-xl font-bold mb-2">{t('intro.title')}</h3>
                  <p className="text-sm opacity-90">{t('intro.subtitle')}</p>
                </div>
              </motion.div>

            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1200 120" className="w-full h-12 md:h-20 fill-white">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"/>
        </svg>
      </div>
    </div>
  );
};

export default Intro;